if (!customElements.get('product-upsell-modal')) {
  customElements.define(
    'product-upsell-modal',
    class ProductUpsellModal extends BasicModal {
      constructor() {
        super();
        this.pendingForm = null;
        this.isSubmitting = false;
        this.skipIntent = false;
        this.confirmIntent = false;
        this.preferredSellingPlanId = null;
        this.preferredSellingPlanName = null;
        this.boundFormCapture = this.onFormSubmitCapture.bind(this);
        this._upsellInitialized = false;
      }

      // Keep in place so show/hide does not disconnect/reconnect and stack listeners.
      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        if (this._upsellInitialized) {
          this.bindProductForms();
          return;
        }
        this._upsellInitialized = true;

        this.cartDrawerElement = document.querySelector('cart-drawer');
        this.itemsContainer = this.querySelector('[data-upsell-items]');
        this.alertElement = this.querySelector('[data-upsell-alert]');
        this.confirmButton = this.querySelector('[data-upsell-confirm]');
        this.skipButton = this.querySelector('[data-upsell-skip]');

        this.config = {
          enabled: this.dataset.enabled === 'true',
          productId: this.dataset.productId,
          allowSkip: this.dataset.allowSkip === 'true',
          requiredCount: Number(this.dataset.requiredCount || 1),
          oncePerSession: this.dataset.oncePerSession === 'true',
          enableRecharge: this.dataset.enableRecharge === 'true',
          showFrequencyPicker: this.dataset.showFrequencyPicker === 'true',
          useSellingPlanCopy: this.dataset.useSellingPlanCopy === 'true',
          selectionRequiredMessage: this.dataset.selectionRequiredMessage || '',
        };

        const offerCount = Number(this.dataset.offerCount || 0);
        if (offerCount > 0) {
          this.config.requiredCount = Math.min(this.config.requiredCount, offerCount);
        }

        this.sessionKey = `product-upsell-seen-${this.config.productId}`;
        this.itemListenerAbort = new AbortController();

        this.confirmButton?.addEventListener('click', this.onConfirm.bind(this), {
          signal: this.itemListenerAbort.signal,
        });
        this.skipButton?.addEventListener('click', this.onSkip.bind(this), {
          signal: this.itemListenerAbort.signal,
        });

        this.itemsContainer?.querySelectorAll('[data-upsell-item]').forEach((item) => {
          item._upsellVariants = this.parseVariantsJson(item);

          const qtyInput = item.querySelector('[data-upsell-quantity]');
          qtyInput?.addEventListener('change', () => this.onQuantityChange(item), {
            signal: this.itemListenerAbort.signal,
          });

          const variantSelect = item.querySelector('[data-variant-select]');
          variantSelect?.addEventListener('change', () => this.onVariantChange(item), {
            signal: this.itemListenerAbort.signal,
          });

          const planSelect = item.querySelector('[data-plan-select]');
          planSelect?.addEventListener('change', () => this.onPlanChange(item), {
            signal: this.itemListenerAbort.signal,
          });

          this.ensureVariantPlanDefaults(item);
          this.syncQuantityNote(item);
        });

        FoxTheme.DOMready(() => this.bindProductForms());
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.unbindProductForms();
        this.itemListenerAbort?.abort();
        this._upsellInitialized = false;
      }

      get hasOffers() {
        return this.itemsContainer?.querySelectorAll('[data-upsell-item]').length > 0;
      }

      getMainProductForms() {
        return Array.from(document.querySelectorAll('form.main-product-form[is="product-form"]')).filter((form) => {
          if (form.closest('quick-view-modal')) return false;
          if (String(form.id || '').includes('quick-view')) return false;
          return true;
        });
      }

      bindProductForms() {
        this.unbindProductForms();
        this.productForms = this.getMainProductForms();
        this.productForms.forEach((form) => {
          form.addEventListener('submit', this.boundFormCapture, true);
        });
      }

      unbindProductForms() {
        this.productForms?.forEach((form) => {
          form.removeEventListener('submit', this.boundFormCapture, true);
        });
        this.productForms = [];
      }

      shouldIntercept() {
        if (!this.config.enabled || !this.hasOffers || this.isSubmitting) return false;
        if (this.open) return false;
        if (this.config.oncePerSession && this.hasSeenThisSession()) return false;
        return true;
      }

      hasSeenThisSession() {
        try {
          return window.sessionStorage.getItem(this.sessionKey) === '1';
        } catch (e) {
          return false;
        }
      }

      markSeenThisSession() {
        try {
          window.sessionStorage.setItem(this.sessionKey, '1');
        } catch (e) {
          // no-op
        }
      }

      onFormSubmitCapture(event) {
        if (!this.shouldIntercept()) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        document.querySelectorAll('sticky-atc-bar-minimalist').forEach((el) => {
          if (typeof el.resetButtonLoading === 'function') el.resetButtonLoading();
        });

        this.pendingForm = event.currentTarget;
        this.clearAlert();
        this.captureTriggerSellingPlan(this.pendingForm);
        this.syncUpsellPlansToTrigger();
        this.show(event.submitter || event.currentTarget);
      }

      getItemQuantity(item) {
        const input = item.querySelector('[data-upsell-quantity]');
        const qty = Number(input?.value);
        if (!Number.isFinite(qty) || qty <= 0) return 0;
        return Math.floor(qty);
      }

      setItemQuantity(item, qty) {
        const input = item.querySelector('[data-upsell-quantity]');
        if (!input) return;
        input.value = String(Math.max(0, Math.floor(Number(qty) || 0)));
        this.syncQuantityNote(item);
      }

      onQuantityChange(item) {
        const input = item.querySelector('[data-upsell-quantity]');
        if (input && (input.value === '' || Number(input.value) < 0)) {
          input.value = '0';
        }
        this.syncQuantityNote(item);
        this.clearAlert();
      }

      syncQuantityNote(item) {
        const note = item.querySelector('[data-upsell-qty-note]');
        const qty = this.getItemQuantity(item);
        item.classList.toggle('has-quantity', qty > 0);
        if (note) note.hidden = qty > 0;
      }

      captureTriggerSellingPlan(form) {
        this.preferredSellingPlanId = null;
        this.preferredSellingPlanName = null;
        if (!form) return;

        const candidates = [
          form.querySelector('[name="selling_plan"]:checked'),
          form.querySelector('select[name="selling_plan"]'),
          form.querySelector('input[name="selling_plan"]'),
          document.querySelector('product-info [name="selling_plan"]:checked'),
          document.querySelector('product-info select[name="selling_plan"]'),
          document.querySelector('product-info input[name="selling_plan"]'),
        ].filter(Boolean);

        const planInput = candidates[0];
        let planId = planInput?.value || new FormData(form).get('selling_plan');
        if (!planId || planId === '0' || planId === '') return;

        this.preferredSellingPlanId = String(planId);

        const labeled =
          planInput?.selectedOptions?.[0]?.textContent ||
          form.querySelector(`label[for="${planInput?.id}"]`)?.textContent ||
          document.querySelector(`label[for="${planInput?.id}"]`)?.textContent ||
          planInput?.getAttribute('data-selling-plan-name') ||
          planInput?.closest('label')?.textContent ||
          '';
        this.preferredSellingPlanName = this.normalizePlanName(labeled);
      }

      normalizePlanName(name) {
        return String(name || '')
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .trim();
      }

      syncUpsellPlansToTrigger() {
        this.itemsContainer?.querySelectorAll('[data-upsell-item]').forEach((item) => {
          this.ensureVariantPlanDefaults(item);
        });
      }

      ensureVariantPlanDefaults(item) {
        const variantSelect = item.querySelector('[data-variant-select]');
        const variantId = variantSelect?.value || item.dataset.variantId;
        const variant = this.getVariantData(item, variantId) || item._upsellVariants?.[0];
        if (!variant) return;

        item.dataset.variantId = String(variant.id);

        if (variantSelect && String(variantSelect.value) !== String(variant.id)) {
          variantSelect.value = String(variant.id);
        }

        this.rebuildPlanSelect(item, variant);
        this.updateCopy(item);
        this.updateVariantNote(item, variant);
      }

      selectedItems() {
        return Array.from(this.itemsContainer.querySelectorAll('[data-upsell-item]')).filter((item) => {
          if (item.classList.contains('is-disabled')) return false;
          return this.getItemQuantity(item) > 0;
        });
      }

      hasValidSelection() {
        // Qty 0 is intentional "no beans" when skip is allowed.
        if (this.config.allowSkip) return true;
        return this.selectedItems().length >= this.config.requiredCount;
      }

      parseVariantsJson(item) {
        const script = item.querySelector('[data-upsell-variants-json]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return [];
        }
      }

      getVariantData(item, variantId) {
        const id = Number(variantId);
        return (item._upsellVariants || []).find((variant) => Number(variant.id) === id);
      }

      onVariantChange(item) {
        const select = item.querySelector('[data-variant-select]');
        if (!select) return;

        const variant = this.getVariantData(item, select.value);
        if (!variant) return;

        item.dataset.variantId = String(variant.id);
        this.updateItemMedia(item, variant.image);
        this.rebuildPlanSelect(item, variant);
        this.updateCopy(item);
        this.updateVariantNote(item, variant);
      }

      updateVariantNote(item, variant = null) {
        const noteEl = item.querySelector('[data-upsell-note]');
        if (!noteEl) return;

        const data = variant || this.getVariantData(item, item.dataset.variantId);
        const note = String(data?.note || '').trim();
        noteEl.textContent = note;
        noteEl.hidden = !note;
      }

      onPlanChange(item) {
        const planSelect = item.querySelector('[data-plan-select]');
        const option = planSelect?.options[planSelect.selectedIndex];
        if (!option) return;

        item.dataset.sellingPlanId = option.value || '';
        this.updateItemPrice(item, Number(option.dataset.price || 0), Number(option.dataset.compareAtPrice || 0));
        this.updateCopy(item);
      }

      updateItemMedia(item, imageUrl) {
        const media = item.querySelector('[data-upsell-media] img');
        if (!media || !imageUrl) return;
        media.src = imageUrl;
        media.srcset = '';
      }

      updateItemPrice(item, price, compareAtPrice) {
        const priceEl = item.querySelector('[data-upsell-price]');
        if (!priceEl) return;
        priceEl.innerHTML = this.formatPriceHtml(price, compareAtPrice);
      }

      formatPriceHtml(price, compareAtPrice) {
        const formatted = FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat);
        if (compareAtPrice > price) {
          const compare = FoxTheme.Currency.formatMoney(compareAtPrice, FoxTheme.settings.moneyFormat);
          return `<span class="f-price f-price--on-sale"><span class="f-price__sale"><s class="f-price-item f-price-item--regular">${compare}</s> <span class="f-price-item f-price-item--sale">${formatted}</span></span></span>`;
        }
        return `<span class="f-price"><span class="f-price__regular"><span class="f-price-item f-price-item--regular">${formatted}</span></span></span>`;
      }

      pickPreferredPlan(plans) {
        if (!plans?.length) return null;

        if (this.preferredSellingPlanId) {
          const byId = plans.find((plan) => String(plan.id) === String(this.preferredSellingPlanId));
          if (byId) return byId;
        }

        if (this.preferredSellingPlanName) {
          const byName = plans.find(
            (plan) => this.normalizePlanName(plan.name) === this.preferredSellingPlanName
          );
          if (byName) return byName;
        }

        return plans[0];
      }

      rebuildPlanSelect(item, variant) {
        const plans = variant.selling_plans || [];
        const planSelect = item.querySelector('[data-plan-select]');
        const planWrap = item.querySelector('[data-plan-wrap]');

        if (!this.config.enableRecharge) {
          item.dataset.sellingPlanId = '';
          this.updateItemPrice(item, variant.price, variant.compare_at_price || 0);
          return;
        }

        if (!plans.length) {
          planWrap?.classList.add('hidden');
          item.dataset.sellingPlanId = '';
          item.classList.add('is-disabled');
          this.setItemQuantity(item, 0);
          return;
        }

        item.classList.remove('is-disabled');
        const preferred = this.pickPreferredPlan(plans);

        if (planSelect) {
          planSelect.innerHTML = '';
          plans.forEach((plan) => {
            const opt = document.createElement('option');
            opt.value = plan.id;
            opt.textContent = plan.name;
            opt.dataset.price = plan.price;
            opt.dataset.compareAtPrice = plan.compare_at_price || 0;
            opt.dataset.planName = plan.name;
            opt.dataset.planDescription = plan.description || '';
            if (preferred && String(preferred.id) === String(plan.id)) opt.selected = true;
            planSelect.appendChild(opt);
          });

          if (planWrap) {
            const showPicker = this.config.showFrequencyPicker && plans.length > 1;
            planWrap.classList.toggle('hidden', !showPicker);
          }
        }

        const selectedPlan = preferred || plans[0];
        item.dataset.sellingPlanId = String(selectedPlan.id);
        this.updateItemPrice(item, selectedPlan.price, selectedPlan.compare_at_price || 0);
        this.updateCopy(item);
      }

      updateCopy(item) {
        const titleEl = item.querySelector('[data-upsell-title]');
        const descEl = item.querySelector('[data-upsell-description]');
        const productTitle = item.dataset.productTitle || '';
        const productDesc = item.dataset.productDescription || '';
        const variant = this.getVariantData(item, item.dataset.variantId);
        const plans = this.config.enableRecharge ? variant?.selling_plans || [] : [];
        const selectedPlan =
          plans.find((plan) => String(plan.id) === String(item.dataset.sellingPlanId)) || plans[0] || null;

        if (titleEl) {
          if (this.config.useSellingPlanCopy && this.config.enableRecharge && selectedPlan?.name) {
            titleEl.textContent = selectedPlan.name;
          } else if (this.config.enableRecharge && plans.length === 1 && selectedPlan?.name) {
            titleEl.textContent = `${productTitle} - ${selectedPlan.name}`;
          } else {
            titleEl.textContent = productTitle;
          }
        }

        if (descEl && this.config.useSellingPlanCopy && this.config.enableRecharge) {
          const text = selectedPlan?.description || productDesc;
          descEl.textContent = text;
          descEl.hidden = !text;
        }
      }

      showSelectionRequiredAlert() {
        if (!this.alertElement) return;
        this.alertElement.hidden = false;
        this.alertElement.textContent = this.config.selectionRequiredMessage;
        this.alertElement.focus?.();
      }

      clearAlert() {
        if (!this.alertElement) return;
        this.alertElement.hidden = true;
        this.alertElement.textContent = '';
      }

      onConfirm(event) {
        event.preventDefault();
        if (!this.hasValidSelection()) {
          this.showSelectionRequiredAlert();
          return;
        }

        this.confirmIntent = true;
        this.hide().then(() => {
          this.addToCart(this.buildItems(true));
        });
      }

      onSkip(event) {
        event.preventDefault();
        this.skipIntent = true;
        this.markSeenThisSession();
        this.hide().then(() => {
          this.addToCart(this.buildItems(false));
        });
      }

      hide() {
        if (!this.open) return Promise.resolve();

        if (!this.confirmIntent && !this.skipIntent && !this.config.allowSkip) {
          this.showSelectionRequiredAlert();
          return Promise.resolve();
        }

        if (!this.confirmIntent && !this.skipIntent && this.config.allowSkip && this.pendingForm) {
          this.markSeenThisSession();
          const items = this.buildItems(false);
          return super.hide().then(() => {
            this.addToCart(items);
          });
        }

        return super.hide().finally(() => {
          this.confirmIntent = false;
          this.skipIntent = false;
        });
      }

      buildItems(includeUpsells) {
        if (!this.pendingForm) return { items: [] };

        const formData = new FormData(this.pendingForm);
        const mainId = Number(formData.get('id'));
        const mainQty = Number(formData.get('quantity') || 1);
        const mainSellingPlan = this.resolveMainSellingPlanId(this.pendingForm, formData);

        const items = [
          {
            id: mainId,
            quantity: mainQty > 0 ? mainQty : 1,
          },
        ];

        if (mainSellingPlan) {
          items[0].selling_plan = mainSellingPlan;
        }

        if (includeUpsells) {
          this.selectedItems().forEach((item) => {
            const variantId = Number(item.dataset.variantId);
            if (!variantId || Number.isNaN(variantId)) return;

            const qty = this.getItemQuantity(item);
            if (qty <= 0) return;

            const line = {
              id: variantId,
              quantity: qty,
            };

            if (this.config.enableRecharge && item.dataset.sellingPlanId) {
              const planId = Number(item.dataset.sellingPlanId);
              if (planId && !Number.isNaN(planId)) {
                line.selling_plan = planId;
              }
            }

            items.push(line);
          });
        }

        return { items };
      }

      resolveMainSellingPlanId(form, formData = null) {
        const data = formData || new FormData(form);
        const fromForm = data.get('selling_plan');
        if (fromForm && fromForm !== '0') {
          const parsed = Number(fromForm);
          if (parsed && !Number.isNaN(parsed)) return parsed;
        }

        // Recharge widgets often keep the control inside product-info, not FormData.
        const productInfo = form.closest('product-info') || document.querySelector('product-info');
        const scope = productInfo || form;
        const external =
          scope.querySelector('[name="selling_plan"]:checked') ||
          scope.querySelector('select[name="selling_plan"]') ||
          scope.querySelector('input[name="selling_plan"]');
        const raw = external?.value;
        if (!raw || raw === '0') return null;
        const parsed = Number(raw);
        return parsed && !Number.isNaN(parsed) ? parsed : null;
      }

      async parseJsonResponse(response) {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (error) {
          throw new Error(
            response.ok
              ? 'Unexpected response from cart. Please try again.'
              : `Cart request failed (${response.status}).`
          );
        }
      }

      getCartJsUrl() {
        const cartUrl = FoxTheme.routes.cart_url || '/cart';
        return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
      }

      getCartAddJsUrl() {
        const addUrl = FoxTheme.routes.cart_add_url || '/cart/add';
        return addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`;
      }

      finishCartSuccess(parsedState, cartJson) {
        if (cartJson) {
          cartJson['sections'] = parsedState?.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        } else if (parsedState) {
          // Add succeeded but cart refresh failed — still notify listeners with add payload.
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
        }

        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: { product: parsedState },
          })
        );

        this.cartDrawerElement && this.cartDrawerElement.show(this.pendingForm);
        this.pendingForm = null;
      }

      addToCart(payload) {
        if (!payload?.items?.length || this.isSubmitting) return;
        if (!payload.items[0]?.id) {
          this.showError('Missing product variant.');
          return;
        }

        this.isSubmitting = true;
        this.setLoading(true);
        this.clearAlert();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          ...payload,
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        fetch(this.getCartAddJsUrl(), {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body,
        })
          .then((response) => this.parseJsonResponse(response))
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showError(parsedState.description || parsedState.message);
              return;
            }

            let cartJson = null;
            try {
              const cartResponse = await fetch(this.getCartJsUrl(), {
                ...FoxTheme.utils.fetchConfig('json', 'GET'),
              });
              cartJson = await this.parseJsonResponse(cartResponse);
            } catch (refreshError) {
              console.warn('Cart refresh failed after successful add:', refreshError);
            }

            this.finishCartSuccess(parsedState, cartJson);
          })
          .catch((error) => {
            console.error(error);
            this.showError(error?.message || 'Error');
          })
          .finally(() => {
            this.isSubmitting = false;
            this.setLoading(false);
            this.confirmIntent = false;
            this.skipIntent = false;
          });
      }

      showError(message) {
        if (this.alertElement) {
          this.alertElement.hidden = false;
          this.alertElement.textContent = message;
        } else if (message) {
          alert(message);
        }

        if (!this.open) this.show();
      }

      setLoading(isLoading) {
        [this.confirmButton, this.skipButton].forEach((button) => {
          if (!button) return;
          button.classList.toggle('btn--loading', isLoading);
          button.toggleAttribute('disabled', isLoading);
        });
      }
    }
  );
}
